import React from "react";
import { connect } from "react-redux";
import { withStyles } from "@material-ui/core";
import { I18n } from "@aws-amplify/core";
import { get, map, filter, size, sortBy, includes } from "lodash";
import Box from "@material-ui/core/Box";

import { fetchGet, fetchPost } from "../../../api/fetch";
import HspCurrentResults from "./HspCurrentResults";
import HspCurrentDecisions from "./HspCurrentDecisions";
import HspCurrentStatusForm from "./HspCurrentStatusForm";
import HspCurrentStatusTable from "./HspCurrentStatusTable";
import Loader from "../../Common/Loader";
import { setBreadcrumb } from "../../../actions/common";
import { fetchChildEventGroups } from "../../../actions/siteEntranceExit/tabs";
import {
  addChildEventGroups,
  extractWalkInOutInfo,
  getReferenceSurvey,
} from "../../../utils/eventGroups";
import { getLov } from "../../../utils/lov";
import { walkInOut } from "../../../constants/reducers";
import { LOG_EVENT_GROUP_TYPES } from "../../../constants/logEventGroupTypes";
import { eventGroupsData } from "../../../constants/eventGroups";

const styles = {
  container: {
    width: "100%",
    maxHeight: "calc(100vh - 100px)",
    overflow: "auto",
    height: "100%",
  },
  gridLine1: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(373px, 1fr))",
    gridAutoRows: "234px",
    gridGap: "24px",
    padding: "0px 24px 24px",
  },
  gridLine2: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gridAutoRows: "max-content",
    gridGap: "24px",
    padding: "0px 24px 24px",
  },
};

const HspCurrentStatus = (props) => {
  const { classes, dispatch, language, assignedSite } = props;
  const [data, setData] = React.useState();
  const [lov, setLov] = React.useState();
  const [isFetching, setIsFetching] = React.useState(false);
  const [filters, setFilters] = React.useState({});
  const [tableFilters, setTableFilters] = React.useState({
    105: [43, 44, 93],
  });

  React.useEffect(() => {
    fetchLov();
    dispatch(
      setBreadcrumb([
        {
          name: "dashboards",
          display: false,
        },
        {
          name: I18n.get("sidebar_hspCurrentStatusDashboard"),
        },
      ])
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLov = async () => {
    try {
      setLov({
        158: await getLov(158),
        105: await getLov(105),
        zones: await fetchGet("zones/search"),
      });
    } catch (e) {
      console.log(e);
    }
  };

  const search = async (filters, ids = null) => {
    if (!ids) setIsFetching(true);
    setFilters(filters);
    try {
      let body = {
        filter: {
          types: get(LOG_EVENT_GROUP_TYPES, walkInOut),
          events: {
            zones: map(
              filter(
                get(lov, ["zones"]),
                (z) => get(filters, ["sites"]) === z.site_id
              ),
              (z) => get(z, ["zone_id"])
            ),
          },
          begin: get(filters, ["date"]),
          end: get(filters, ["date"]) + 86400000,
        },
        data: eventGroupsData,
      };
      if (ids) {
        body.filter.ids = ids;
      }
      let res = await fetchPost("new/event_groups", body);
      res = map(res.items, (item) =>
        extractWalkInOutInfo(item, () => ({ user: { language } }), lov)
      );
      let childEventGroups = await fetchChildEventGroups(
        walkInOut,
        map(res, (item) => item.id)
      );
      res = map(res, (item) =>
        addChildEventGroups(
          walkInOut,
          item,
          filter(childEventGroups, (i) => i.parent.id === item.id),
          lov,
          language
        )
      );

      for (let i = 0; i < size(res); i++) {
        let group = get(res, [i]);
        if (get(group, ["human", "verification", 0, "surveyIdToFetch"])) {
          let instructionId = get(group, [
            "human",
            "verification",
            0,
            "instruction_id",
          ]);
          group["human"].verification[0] = await getReferenceSurvey(
            get(group, ["human", "verification", 0, "surveyIdToFetch"]),
            lov,
            language,
            get(group, ["human", "verification", 0, "instruction"]),
            get(group, ["human", "verification", 0, "instructionColor"])
          );
          group["human"].verification[0].instruction_id = instructionId;
        }
      }
      if (ids) {
        setData(
          sortBy(
            [...res, ...filter(data, (item) => !includes(ids, item.id))],
            (item) => -new Date(get(item, ["main", "dateTime"]))
          )
        );
      } else {
        setData(
          sortBy(res, (item) => -new Date(get(item, ["main", "dateTime"])))
        );
      }
    } catch (e) {
      console.log(e);
    }
    setIsFetching(false);
  };

  return (
    <Box className={classes.container}>
      <Box className="text-54 text-italic" style={{ margin: "8px 24px -16px" }}>
        {I18n.get("topbar_hspCurrentStatusDescription")}
      </Box>
      <HspCurrentStatusForm
        search={search}
        isFetching={isFetching}
        assignedSite={assignedSite}
      />
      {isFetching ? (
        <Loader noDecoration height={"calc(100% - 116px)"} />
      ) : data ? (
        <>
          <Box className={classes.gridLine1}>
            <HspCurrentResults data={data} />
            <HspCurrentDecisions
              data={data}
              tableFilters={tableFilters}
              setTableFilters={setTableFilters}
            />
          </Box>
          <Box className={classes.gridLine2}>
            <HspCurrentStatusTable
              data={data}
              lov={lov}
              filters={tableFilters}
              setFilters={setTableFilters}
              refresh={(ids) => search(filters, ids)}
            />
          </Box>
        </>
      ) : (
        <></>
      )}
    </Box>
  );
};

const mapStateToProps = (state) => {
  return {
    language: get(state, ["user", "language"]),
    assignedSite: get(state, ["user", "assignedSite"]),
  };
};

export default connect(mapStateToProps)(withStyles(styles)(HspCurrentStatus));
